/**
 * MessengerBusinessLogin — conexión de Messenger vía Facebook Login for Business.
 *
 * POR QUÉ REEMPLAZA AL REDIRECT (07-ago-2026)
 * ───────────────────────────────────────────
 * El flujo anterior mandaba al usuario a `dialog/oauth?scope=…` y volvía por un
 * redirect al backend. Funcionaba, pero el token que emite **expira a los 60
 * días**: cada cliente tendría que reconectar cada dos meses, y cuando el token
 * muere el canal se apaga en silencio.
 *
 * `FB.login` con `config_id` devuelve un Business Integration System User token
 * que NO expira, asociado al portfolio del cliente en vez de a una persona.
 *
 * Está calcado de WhatsAppEmbeddedSignup.jsx, que usa el mismo mecanismo y está
 * validado con tráfico real. Se eligió replicar ese patrón en vez de pasarle
 * `config_id` al redirect de servidor porque la documentación del flujo manual
 * NO menciona ese parámetro, y una sonda al diálogo no logra distinguir un
 * config_id válido de uno inventado (los dos devuelven 302).
 *
 * SIN `extras`: los del Embedded Signup (`feature: 'whatsapp_embedded_signup'`)
 * son específicos de ese producto.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { auth } from '../../firebase';
import useFacebookSDK from '../../hooks/useFacebookSDK';
import { toast } from '../../components/Toast';

const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-sistema-bks.cloudfunctions.net';

// Configuración de Facebook Login for Business para PÁGINAS.
// Creada el 07-ago-2026: token System-user, expiración NUNCA, activo Páginas,
// permisos business_management + pages_manage_metadata + pages_messaging +
// pages_read_engagement + pages_show_list.
// Es distinta de la del Embedded Signup de WhatsApp (954353037566169).
const MESSENGER_CONFIG_ID = '27703259986021939';

export default function MessengerBusinessLogin({ organizationId = null, botId = null, onConnected }) {
  const { t } = useTranslation('channels');
  const { FB, loading: sdkLoading, error: sdkError } = useFacebookSDK();

  const [enviando, setEnviando] = useState(false);
  const [code, setCode] = useState(null);
  const enviadoRef = useRef(false);

  const listo = !!FB && !sdkLoading && !sdkError;

  // El POST vive acá y NO dentro del callback de FB.login.
  //
  // El SDK de Facebook exige un callback SÍNCRONO: pasarle una `async function`
  // hace que tire "Expression is of type asyncfunction, not function" y **el popup
  // nunca se abre**. Lo aprendimos rompiéndolo — y el patrón correcto ya estaba
  // en WhatsAppEmbeddedSignup.jsx: el callback sólo guarda el `code` en estado, y
  // el trabajo asíncrono lo dispara un efecto.
  //
  // `enviadoRef` evita el doble POST: en desarrollo React monta dos veces
  // (StrictMode) y sin la guarda se canjearía el mismo `code` dos veces — el
  // segundo canje falla porque Meta los invalida al usarlos.
  useEffect(() => {
    if (!code || enviadoRef.current) return;
    enviadoRef.current = true;

    (async () => {
      try {
        const idToken = await auth.currentUser.getIdToken();
        const res = await fetch(`${API_URL}/integrations_messenger_business_callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ code, organizationId, botId }),
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data.ok) {
          console.error('[MessengerBusinessLogin] backend error', data);
          toast.error(
            t(`oauthErrors.${data.error}`, {
              cuenta: data.cuenta || '',
              defaultValue: t('oauthErrors.generico', { codigo: data.error || res.status }),
            }),
            15000
          );
          return;
        }

        // El tipo de token es el criterio de éxito de la migración, no un detalle:
        // si vuelve USER, la conexión funciona pero caduca igual que antes y la
        // configuración de Login for Business no está emitiendo lo que se le pidió.
        // Se avisa acá para no depender de leer logs.
        if (data.tokenType !== 'SYSTEM_USER') {
          toast.info(
            `Conectado, pero el token es de tipo ${data.tokenType || 'desconocido'} y va a caducar. ` +
            `Revisá la configuración de Login for Business.`,
            20000
          );
        } else {
          toast.success(
            t('oauthSuccess.messenger_connected', { defaultValue: 'Messenger conectado.' })
          );
        }

        // Sin `window.location.reload()`: la lista de cuentas de /canales es
        // onSnapshot (Canales.jsx:304), así que se actualiza sola. Recargar además
        // borraría el toast antes de que se pueda leer — y el aviso del tipo de
        // token es justo el que hay que leer.
        if (onConnected) onConnected(data);
      } catch (e) {
        console.error('[MessengerBusinessLogin] network error', e);
        toast.error(
          t('oauthErrors.internal_error', { defaultValue: 'Hubo un error al conectar.' }),
          12000
        );
      } finally {
        setEnviando(false);
        setCode(null);
        enviadoRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const conectar = () => {
    if (!listo || enviando) return;
    setEnviando(true);

    // Callback SÍNCRONO. Ver el comentario del efecto de arriba.
    FB.login(
      (response) => {
        const c = response?.authResponse?.code;
        if (c) {
          setCode(c);
        } else {
          // Cerrar el popup no es un error que merezca un toast rojo: es una
          // decisión del usuario.
          console.log('[MessengerBusinessLogin] sin code (popup cerrado o cancelado)');
          setEnviando(false);
        }
      },
      {
        config_id: MESSENGER_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
      }
    );
  };

  return (
    <button
      onClick={conectar}
      disabled={!listo || enviando}
      className="connect-btn"
      style={{
        backgroundColor: !listo || enviando ? '#a0aec0' : '#0084ff',
        color: 'white',
        cursor: !listo || enviando ? 'not-allowed' : 'pointer',
      }}
    >
      {enviando
        ? t('messenger.connecting', { defaultValue: 'Conectando…' })
        : t('social.connect', { provider: 'Messenger' })}
    </button>
  );
}
